import { useState, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import withPermissionProtection from 'hoc/withPermissionProtection';
import SnackbarContext from 'context/Snackbar/Context';

import Grid from '@material-ui/core/Grid';
import Button from 'components/CncUI/Button';
import TextFieldSsn from 'components/CncUI/TextField/TextFieldSsn';
import Typography from 'components/CncUI/Typography';
import List from './List';
import Details from './Details';

import useQuery from 'hooks/useQuery';
import Valid from 'utilities/valid';
import format from 'utilities/format';
import SearchIcon from '@material-ui/icons/Search';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  header: {
    marginTop: 40,
    marginBottom: 0,
    [breakpoints.up('lg')]: {
      fontSize: 48,
      lineHeight: '56px',
    },
  },
  searchContainer: {
    width: 460,
    [breakpoints.down('sm')]: {
      width: 360,
    },
  },
  searchType: {
    minWidth: 68,
    [breakpoints.up('md')]: {
      minWidth: 100,
    },
  },
  searchField: {
    [breakpoints.up('md')]: {
      width: 300,
    },
  },
  resultsContainer: {
    backgroundColor: 'white',
    border: `1px dashed ${palette.cncUI.default['-5']}`,
    minHeight: 420,
    margin: 8,
    flex: 1,
    minWidth: 460,
    [breakpoints.down('sm')]: {
      minWidth: 300,
    },
  },
  resultsText: {
    marginBottom: 15,
  },
}));

const CCPA = () => {
  const classes = useStyles();
  const notify = useContext(SnackbarContext);
  const getProfileInformation = useQuery('getCCPAEmployeeInformation', {
    scopes: 'ccpa:access',
    fetchPolicy: 'network-only',
    request: ssn => ({ input: { ssn } }),
    response: res => ({
      mappings: res.data.getProfileMappingOptions,
      data: res.data.getCCPAEmployeeInformation,
    }),
  });
  const [state, setState] = useState({
    loading: false,
    called: false,
    searchData: [],
    profiles: [],
    mappingOptions: [],
    message: '',
    selected: null,
    ssn: '',
    searchType: 'SSN',
  });
  const modify = data => setState(old => ({ ...old, ...data }));

  const search = () => {
    modify({ called: true, loading: true, message: '' });
    getProfileInformation(state.ssn)
      .then(({ data: { profiles, search }, mappings }) => {
        const count = (profiles && profiles.length) || 0;
        const plural = count !== 1 ? 's' : '';
        modify({
          profiles,
          selected: null,
          searchData: search,
          mappingOptions: mappings,
          message: `${count} result${plural} for "${
            state.searchType === 'SIN' ? format.sin(state.ssn) : state.ssn
          }"`,
        });
      })
      .catch(err => {
        modify({
          profiles: [],
          mappingOptions: [],
          searchData: [],
          selected: null,
        });
        notify({
          type: 'error',
          title: 'Error',
          message: err.message.replace('GraphQL error: ', ''),
        });
      })
      .finally(() => {
        modify({ loading: false });
      });
  };

  const keyDown = e => {
    const { charCode, keyCode } = e || {};
    const enterCode = 13;
    if (charCode === enterCode || keyCode === enterCode) {
      e.preventDefault();
      e.stopPropagation();
      if (Valid.ssn(state.ssn)) search();
    }
  };

  return (
    <Grid container spacing={2} id="EmployeeLookup">
      <Grid item xs={12} id="HeaderTitle">
        <Typography variant="h2" className={classes.header}>
          Employee Profile Lookup (CCPA)
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2" size="long" id="HeaderBody">
          California residents can only request their info 2 times within a
          rolling 12 month period.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item className={classes.searchContainer}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={7} className={classes.searchField}>
                <TextFieldSsn
                  light
                  fullWidth
                  onKeyDown={keyDown}
                  label="Full SSN"
                  startIcon={<SearchIcon />}
                  value={state.ssn}
                  onChange={e => modify({ ssn: e.target.value })}
                  showAll
                  id="SearchField"
                />
              </Grid>
              <Grid item xs={5} style={{ flex: 1 }}>
                <Button
                  fullWidth
                  id="SearchButton"
                  loading={state.loading}
                  onClick={search}
                  disabled={!Valid.ssn(state.ssn)}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
            <Grid item xs={12} style={{ marginTop: 16 }}>
              <Typography
                italic
                variant="caption"
                className={classes.resultsText}
                id="ResultsMessage"
              >
                {state.called && state.message}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {!state.loading && (
                <List
                  profiles={state.profiles}
                  search={state.searchData}
                  onSelect={selected => modify({ selected })}
                />
              )}
            </Grid>
          </Grid>
          <Grid item className={classes.resultsContainer}>
            {state.called && (
              <Details
                loading={state.loading}
                notFound={state.profiles && state.profiles.length === 0}
                profile={state.selected}
                mappingOptions={state.mappingOptions}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default withPermissionProtection('ccpa:access')(CCPA);
